/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/assembly/AssetsPage.tsx
 * ----------------------------------------------------------------------------
 * Assembly • Assets (unidades seriadas)
 *
 * Versão 1:
 *  - Listagem de unidades seriadas (Assets) com filtros simples.
 *  - Consome a API: GET /api/assembly/assets/
 *  - Filtros suportados (querystring):
 *      • status=IN_STOCK|RESERVED|INSTALLED|REMOVED|SCRAPPED|RETURNED
 *      • search=texto livre (produto / série / cliente, conforme backend)
 * ========================================================================== */

import React, { useEffect, useState, FormEvent } from "react";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

/* -------------------------------------------------------------------------- */
/* Tipos e helpers                                                             */
/* -------------------------------------------------------------------------- */

type AssetStatus =
    | "IN_STOCK"
    | "RESERVED"
    | "INSTALLED"
    | "REMOVED"
    | "SCRAPPED"
    | "RETURNED"
    | string;

type AssetDto = {
    id: number;
    // campos principais (podem vir como ID ou já “enriquecidos” pelo serializer)
    product?: number | null;
    product_code?: string | null;
    product_name?: string | null;

    serial_number?: string | null;
    lot_code?: string | null;

    status?: AssetStatus;

    current_owner?: number | null;
    current_owner_name?: string | null;

    root_asset?: number | null;
    root_asset_serial?: string | null;
    root_asset_product_code?: string | null;

    installed_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;

    // qualquer extra que o backend envie
    [key: string]: any;
};

const STATUS_LABELS: Record<string, string> = {
    IN_STOCK: "Em stock",
    RESERVED: "Reservado",
    INSTALLED: "Instalado",
    REMOVED: "Removido",
    SCRAPPED: "Sucata / descartado",
    RETURNED: "Devolvido",
};

const API_BASE =
    (import.meta as any).env.VITE_API_BASE_URL?.replace(/\/$/, "") || "/api";
const ASM_BASE = `${API_BASE}/assembly`;

/** Monta headers com Authorization (quando existir token). */
function buildAuthHeaders(): HeadersInit {
    const state: any = (useAuthStore as any)?.getState?.() ?? {};
    const token: string | undefined = state.accessToken;
    const headers: HeadersInit = {
        "Content-Type": "application/json",
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
}

/** Formata data "YYYY-MM-DD" ou datetime ISO para "dd/MM/yyyy". */
function formatDate(dateStr?: string | null): string {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}/${month}/${year}`;
}

/* -------------------------------------------------------------------------- */
/* Componente principal                                                        */
/* -------------------------------------------------------------------------- */

const DEFAULT_STATUS: string = ""; // “todos”

export default function AssetsPage() {
    const [status, setStatus] = useState<string>(DEFAULT_STATUS);
    const [search, setSearch] = useState<string>("");

    const [items, setItems] = useState<AssetDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(
        "Consulta de unidades seriadas (Assets) com rastreio de montagem. Em breve vamos permitir criar e ligar componentes diretamente a partir desta tela."
    );

    async function loadAssets(): Promise<void> {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams();
            if (status) params.append("status", status);
            if (search.trim().length > 0) params.append("search", search.trim());

            const qs = params.toString();
            const url = `${ASM_BASE}/assets/${qs ? `?${qs}` : ""}`;

            const res = await fetch(url, {
                method: "GET",
                headers: buildAuthHeaders(),
                credentials: "include",
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                setError(
                    `Erro ao carregar assets (HTTP ${res.status}). ${text}`.trim()
                );
                setItems([]);
                setInfo(null);
                return;
            }

            const data = await res.json();

            let results: AssetDto[] = [];
            if (Array.isArray(data)) {
                results = data as AssetDto[];
            } else if (Array.isArray(data?.results)) {
                results = data.results as AssetDto[];
            }

            setItems(results);

            if (results.length === 0) {
                setInfo(
                    "Nenhum asset encontrado para os filtros selecionados. Experimente alargar o período ou remover filtros."
                );
            } else {
                setInfo(null);
            }
        } catch (e) {
            console.error(e);
            setError("Erro de comunicação com o backend ao carregar assets.");
            setItems([]);
            setInfo(null);
        } finally {
            setLoading(false);
        }
    }

    // Carrega na primeira abertura
    useEffect(() => {
        void loadAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleSubmit(e: FormEvent) {
        e.preventDefault();
        void loadAssets();
    }

    /* -------------------------------- render --------------------------------- */

    return (
        <div className="p-4 lg:px-6 lg:py-6 space-y-6">
            {/* Cabeçalho ----------------------------------------------------------- */}
            <header className="space-y-1">
                <h1 className="text-2xl font-semibold">
                    Assembly • Assets (unidades seriadas)
                </h1>
                <p className="text-sm text-slate-300">
                    Consulta das unidades seriadas (variadores, quadros, máquinas, etc.)
                    com informação de estado, cliente e raiz de montagem. Este módulo
                    suporta a rastreabilidade ponta-a-ponta dos componentes instalados.
                </p>
            </header>

            {/* Mensagens de estado ------------------------------------------------- */}
            {error && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-100">
                    {error}
                </div>
            )}
            {info && !error && (
                <div className="rounded-md border border-sky-500/60 bg-sky-500/10 px-4 py-2 text-sm text-sky-100">
                    {info}
                </div>
            )}

            {/* Filtros ------------------------------------------------------------- */}
            <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-wrap items-end gap-3"
                >
                    <div className="space-y-1">
                        <label className="text-xs font-medium" htmlFor="asm-status">
                            Estado
                        </label>
                        <select
                            id="asm-status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-44 rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                        >
                            <option value="">Todos</option>
                            <option value="IN_STOCK">Em stock</option>
                            <option value="RESERVED">Reservado</option>
                            <option value="INSTALLED">Instalado</option>
                            <option value="REMOVED">Removido</option>
                            <option value="SCRAPPED">Sucata / descartado</option>
                            <option value="RETURNED">Devolvido</option>
                        </select>
                    </div>

                    <div className="space-y-1 min-w-[220px]">
                        <label className="text-xs font-medium" htmlFor="asm-search">
                            Pesquisa
                        </label>
                        <Input
                            id="asm-search"
                            type="text"
                            placeholder="Produto, nº de série, cliente/obra…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-64 md:w-80"
                        />
                    </div>

                    <div className="flex items-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setStatus(DEFAULT_STATUS);
                                setSearch("");
                                void loadAssets();
                            }}
                        >
                            Limpar filtros
                        </Button>
                        <Button type="submit" size="sm" disabled={loading}>
                            {loading ? "A carregar..." : "Atualizar"}
                        </Button>
                    </div>
                </form>
            </section>

            {/* Tabela -------------------------------------------------------------- */}
            <div className="border border-slate-800 rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[260px]">Produto / Série</TableHead>
                            <TableHead className="w-[140px]">Estado</TableHead>
                            <TableHead className="w-[220px]">Cliente / Obra</TableHead>
                            <TableHead className="w-[220px]">Root asset (máquina)</TableHead>
                            <TableHead className="w-[140px]">Instalação</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    A carregar assets...
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    Nenhum asset encontrado para os filtros atuais.
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : (
                            items.map((asset) => {
                                const productCode =
                                    asset.product_code ??
                                    asset.root_asset_product_code ??
                                    "-";
                                const productName = asset.product_name ?? "";
                                const serial = asset.serial_number ?? "—";

                                const statusLabel =
                                    STATUS_LABELS[asset.status ?? ""] ?? asset.status ?? "—";

                                const ownerName = asset.current_owner_name ?? "—";
                                const rootSerial = asset.root_asset_serial ?? "—";

                                return (
                                    <TableRow key={asset.id}>
                                        <TableCell className="text-sm">
                                            <div className="font-medium">
                                                {productCode || "Sem código"}{" "}
                                                {serial !== "—" && (
                                                    <span className="text-xs text-slate-400">
                                                        • Nº série {serial}
                                                    </span>
                                                )}
                                            </div>
                                            {productName && (
                                                <div className="text-xs text-slate-400">
                                                    {productName}
                                                </div>
                                            )}
                                            {asset.lot_code && (
                                                <div className="text-xs text-slate-500">
                                                    Lote: {asset.lot_code}
                                                </div>
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <span className="inline-flex items-center rounded-full border border-emerald-500/60 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-100">
                                                {statusLabel}
                                            </span>
                                        </TableCell>

                                        <TableCell className="text-sm text-slate-200">
                                            {ownerName}
                                        </TableCell>

                                        <TableCell className="text-sm text-slate-200">
                                            {rootSerial === "—" ? (
                                                <span className="text-slate-500">—</span>
                                            ) : (
                                                <>
                                                    Nº série raiz:{" "}
                                                    <span className="font-mono text-xs">
                                                        {rootSerial}
                                                    </span>
                                                </>
                                            )}
                                        </TableCell>

                                        <TableCell className="text-sm">
                                            {asset.installed_at ? (
                                                formatDate(asset.installed_at)
                                            ) : (
                                                <span className="text-slate-500">—</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
